/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { Colors, TypographyPDL } from '@zapier/design-system';
import type { LiteralUnion } from '../types';

import { useContext, useEffect, useRef, useState } from 'react';
import type { TooltipProps as ReactAriaTooltipProps } from 'react-aria-components';
import {
  Tooltip as ReactAriaTooltip,
  TooltipTrigger,
  TooltipTriggerStateContext,
} from 'react-aria-components';
import { FocusableTrigger } from './FocusableTrigger';

const LINE_HEIGHT = 24;
const PADDING_Y = 2;
const SINGLE_LINE_HEIGHT = LINE_HEIGHT + PADDING_Y * 2;

const TooltipStyles = ({
  allowMultiline,
  isMultiline,
}: {
  allowMultiline: TooltipProps['allowMultiline'];
  isMultiline: boolean;
}) =>
  css([
    TypographyPDL('--zds-body-1', 'paragraph3Semibold'),
    {
      background: `var(--zds-gray-warm-10, ${Colors.neutral800})`,
      borderRadius: 'var(--zds-radius-medium, 100px)',
      color: `var(--zds-gray-warm-1, ${Colors.neutral100})`,
      padding: `var(--zds-space-2, 2px) var(--zds-space-12, 11px)`,
      width: 'max-content',
      maxWidth: '300px',
    },
    // Truncates tooltip to a single line if tooltip isn't allowed to span multiple lines
    !allowMultiline && {
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    },
    // Applies multi-line styles only if content actually spans multiple lines
    isMultiline && {
      ...TypographyPDL('--zds-body-1-multiline', 'paragraph3Semibold'),
      borderRadius: 'var(--zds-radius-medium, 10px)',
      padding: 'var(--zds-space-4, 16px) var(--zds-space-8, 14px)',
    },
  ]);

export const TOOLTIP_PLACEMENTS = [
  'bottom',
  'bottom left',
  'bottom right',
  'top',
  'top left',
  'top right',
  'left',
  'left top',
  'left bottom',
  'right',
  'right top',
  'right bottom',
] as const;

type TooltipPlacement = (typeof TOOLTIP_PLACEMENTS)[number];

const OFFSET_VALUES = {
  small: 4,
  medium: 8,
  large: 12,
};

const getOffsetValue = (offset: TooltipProps['offset'] = 'medium'): number => {
  if (typeof offset === 'number') {
    return offset;
  }

  return OFFSET_VALUES[offset];
};

export type TooltipProps = {
  /** Whether the tooltip content can extend to multiple lines. */
  allowMultiline?: boolean;
  /** The trigger to toggle the tooltip. Currently only the beta `IconButton` is supported. */
  children: React.ReactNode;
  /** The content inside the tooltip. */
  content: string;
  /** Whether the toolip is open. Should only be passed when the component's state is controlled. */
  isOpen?: ReactAriaTooltipProps['isOpen'];
  offset?: 'small' | 'medium' | 'large' | number;
  /** Handler called when the tooltip's open state changes. Should only be passed when the component's state is controlled. */
  onOpenChange?: ReactAriaTooltipProps['onOpenChange'];
  /** The placement of the element with respect to its anchor element. */
  placement?: LiteralUnion<
    TooltipPlacement,
    ReactAriaTooltipProps['placement']
  >;
};

/**
 * Displays a description of an element while hovering over or focusing on that element.
 */
export const Tooltip = ({
  allowMultiline = false,
  children,
  content,
  isOpen,
  offset = 'medium',
  onOpenChange,
  placement = 'bottom',
}: TooltipProps) => {
  return (
    <TooltipTrigger
      closeDelay={200}
      delay={200}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      {children}
      <TooltipContent
        allowMultiline={allowMultiline}
        content={content}
        offset={offset}
        placement={placement}
      />
    </TooltipTrigger>
  );
};

type TooltipContentProps = Required<
  Pick<TooltipProps, 'allowMultiline' | 'content' | 'offset' | 'placement'>
>;

/**
 * Wrapper around Tooltip content. It calculates the Tooltip's height to apply the correct styles to multi-line content.
 * @private
 */
const TooltipContent = ({
  allowMultiline,
  content,
  offset,
  placement,
}: TooltipContentProps) => {
  const { isOpen } = useContext(TooltipTriggerStateContext);
  const [isMultiline, setIsMultiLine] = useState(allowMultiline);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (tooltipRef.current) {
      const tooltipHeight = tooltipRef.current.getBoundingClientRect().height;
      const isMultiLine = tooltipHeight > SINGLE_LINE_HEIGHT;
      setIsMultiLine(isMultiLine);
    }
  }, [isOpen]);

  return (
    <ReactAriaTooltip
      css={TooltipStyles({ allowMultiline, isMultiline })}
      data-zds
      offset={getOffsetValue(offset)}
      placement={placement}
      ref={tooltipRef}
    >
      {content}
    </ReactAriaTooltip>
  );
};

Tooltip.FocusableTrigger = FocusableTrigger;

export default Tooltip;
